import React, { useMemo, useRef } from 'react';
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import {
  MarkdownTableGrid,
  type MarkdownTableEditHandlers,
} from '@cotera/client/app/components/ui/data-grid/markdown-table';
import { createTableEditHandlers, deriveTableData } from './table-model';

/**
 * Renders a ProseMirror table with the shared {@link MarkdownTableGrid}. The
 * table's content is not editable through ProseMirror here; instead the grid
 * drives edits back into the document via transactions in
 * {@link createTableEditHandlers}, so the stock table schema — and its markdown
 * serialization/paste — stays intact.
 *
 * The `table` node has content, so `@tiptap/react` always creates a content DOM
 * for it. When no `NodeViewContent` is mounted it appends that content DOM to
 * the wrapper (visible raw `<tr>`/`<th>` rows rendered under the grid, since
 * tiptap 3.28). We therefore mount a hidden `NodeViewContent` to give the
 * content DOM a defined, offscreen home rather than letting it render alongside
 * the grid. The grid reads from the node model, so hiding the content DOM does
 * not affect it.
 */
export const TableGridNodeView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
}) => {
  // Handlers created once (keyed on editability) fire long after this render, so
  // they read the current node/position through refs rather than a stale close.
  const nodeRef = useRef(node);
  nodeRef.current = node;
  const getPosRef = useRef(getPos);
  getPosRef.current = getPos;

  const data = useMemo(() => deriveTableData(editor, node), [editor, node]);

  const isEditable = editor.isEditable;
  const edit = useMemo<MarkdownTableEditHandlers | undefined>(
    () =>
      isEditable
        ? createTableEditHandlers(
            editor,
            () => nodeRef.current,
            () => getPosRef.current()
          )
        : undefined,
    [editor, isEditable]
  );

  return (
    <NodeViewWrapper
      className="table-grid-node react-renderer my-4"
      data-drag-handle
    >
      <div contentEditable={false} suppressContentEditableWarning>
        <MarkdownTableGrid data={data} edit={edit} />
      </div>
      {/* Hidden home for the table's ProseMirror content DOM; see the note
          above. Kept out of view so only the grid renders. */}
      <NodeViewContent as="div" style={{ display: 'none' }} aria-hidden />
    </NodeViewWrapper>
  );
};
